import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { announcementAPI, agencyAPI } from '@/lib/api';
import { isKRRole, formatDate, getErrorMessage } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Megaphone, Plus, Edit, Trash2, Loader2, Eye, EyeOff, Image as ImageIcon, X } from 'lucide-react';
import ImageCropper16x9 from '@/components/common/ImageCropper16x9';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import DashboardBannerManager from '@/components/announcements/DashboardBannerManager';

const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'All Users' },
  { value: 'agents_only', label: 'Agents Only' },
  { value: 'agencies_only', label: 'Agencies Only' },
  { value: 'specific_agency', label: 'Specific Agency' },
  { value: 'specific_roles', label: 'Specific Roles' },
];

const ALL_ROLES = [
  'KR_SuperAdmin', 'KR_AdminOps', 'Agency_Principal', 'Agency_User', 'Agent',
];

export default function AnnouncementsPage() {
  const { user } = useAuth();
  const isKR = isKRRole(user);
  const [tab, setTab] = useState('announcements');
  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({
    title: '', message: '', audience: 'all', target_agency_id: '',
    target_roles: [], expiration_date: '', active: true,
  });
  const [pendingCover, setPendingCover] = useState(null);       // File chosen before create
  const [uploadingCover, setUploadingCover] = useState(false);
  const [cropTarget, setCropTarget] = useState(null);           // File waiting to be cropped 16:9

  const load = useCallback(async () => {
    try {
      const [annRes, agRes] = await Promise.all([
        announcementAPI.list(),
        isKR ? agencyAPI.list() : Promise.resolve({ data: [] }),
      ]);
      setAnnouncements(annRes.data);
      setAgencies(agRes.data);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [isKR]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', message: '', audience: 'all', target_agency_id: '', target_roles: [], expiration_date: '', active: true });
    setPendingCover(null);
    setShowDialog(true);
  };

  const openEdit = (ann) => {
    setEditing(ann);
    setForm({
      title: ann.title, message: ann.message, audience: ann.audience,
      target_agency_id: ann.target_agency_id || '',
      target_roles: ann.target_roles || [],
      expiration_date: ann.expiration_date || '', active: ann.active,
    });
    setPendingCover(null);
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.message.trim()) {
      toast.error('Title and message are required');
      return;
    }
    try {
      const payload = { ...form };
      if (payload.audience !== 'specific_agency') payload.target_agency_id = null;
      if (payload.audience !== 'specific_roles') payload.target_roles = null;
      if (!payload.expiration_date) payload.expiration_date = null;
      // pendingCover is a File selected but not yet uploaded (only
      // possible during creation — an id doesn't exist yet).
      const pending = pendingCover;
      let saved;
      if (editing) {
        const r = await announcementAPI.update(editing.announcement_id, payload);
        saved = r.data;
        toast.success('Announcement updated');
      } else {
        const r = await announcementAPI.create(payload);
        saved = r.data;
        toast.success('Announcement published');
      }
      if (pending && saved?.announcement_id) {
        try {
          await announcementAPI.uploadCover(saved.announcement_id, pending);
        } catch (upErr) {
          toast.error('Cover upload failed — you can retry from Edit.');
        }
      }
      setPendingCover(null);
      setShowDialog(false);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const handleCoverPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\/(png|jpeg|webp|gif)$/.test(file.type)) {
      toast.error('Cover must be PNG, JPEG, WebP or GIF');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Cover image must be ≤ 5 MB');
      return;
    }
    // Route through the 16:9 cropper so every card renders consistently.
    setCropTarget(file);
  };

  const handleCropped = async (file) => {
    setCropTarget(null);
    // In edit mode we upload immediately so the operator sees the new
    // cover reflected in the card. In create mode we defer until Publish.
    if (editing) {
      try {
        setUploadingCover(true);
        const r = await announcementAPI.uploadCover(editing.announcement_id, file);
        setEditing({ ...editing, cover_image_url: r.data.cover_image_url });
        toast.success('Cover updated');
        load();
      } catch (err) {
        toast.error(getErrorMessage(err, 'Cover upload failed'));
      } finally {
        setUploadingCover(false);
      }
    } else {
      setPendingCover(file);
    }
  };

  const handleCoverDelete = async () => {
    if (editing?.cover_image_url) {
      try {
        await announcementAPI.deleteCover(editing.announcement_id);
        setEditing({ ...editing, cover_image_url: null });
        toast.success('Cover removed');
        load();
      } catch (err) {
        toast.error(getErrorMessage(err, 'Failed to remove cover'));
      }
    } else {
      setPendingCover(null);
    }
  };

  const handleDelete = async () => {
    try {
      await announcementAPI.delete(deleteTarget.announcement_id);
      toast.success('Announcement deleted');
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const toggleActive = async (ann) => {
    try {
      await announcementAPI.update(ann.announcement_id, { active: !ann.active });
      toast.success(ann.active ? 'Deactivated' : 'Activated');
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const toggleRole = (role) => {
    setForm(prev => {
      const roles = prev.target_roles.includes(role)
        ? prev.target_roles.filter(r => r !== role)
        : [...prev.target_roles, role];
      return { ...prev, target_roles: roles };
    });
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin" size={32} /></div>;

  return (
    <div data-testid="announcements-page">
      <PageHeader
        title="Announcements"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Announcements', href: '/announcements' },
        ]}
        actions={isKR && tab === 'announcements' && (
          <Button onClick={openCreate} data-testid="create-announcement-btn">
            <Plus size={16} className="mr-2" /> New Announcement
          </Button>
        )}
      />

      <div className="page-content space-y-4">
        {isKR && (
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList>
              <TabsTrigger value="announcements" data-testid="tab-announcements">Announcements</TabsTrigger>
              <TabsTrigger value="banners" data-testid="tab-dashboard-banners">Dashboard Banners</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
        {(!isKR || tab === 'announcements') && (
          <div className="space-y-4">
        {announcements.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-sm text-slate-500">
            <Megaphone size={32} className="mx-auto mb-3 text-slate-300" />
            <p className="font-medium text-slate-700">No announcements</p>
            <p>Create your first announcement to notify users.</p>
          </CardContent></Card>
        ) : (
          announcements.map(ann => (
            <Card key={ann.announcement_id} className={`border-l-4 overflow-hidden ${ann.active ? 'border-l-blue-500' : 'border-l-slate-300 opacity-60'}`} data-testid={`announcement-${ann.announcement_id}`}>
              {/* Phase 27.9 — cover banner. Rendered above the content
                  so KR announcements (incentives, carrier logos, events)
                  read on-brand instead of as plain text. */}
              {ann.cover_image_url && (
                <div className="w-full bg-slate-100 border-b aspect-video" data-testid={`announcement-cover-${ann.announcement_id}`}>
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${ann.cover_image_url}`}
                    alt={ann.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              )}
              <CardContent className="py-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="text-sm font-semibold text-slate-900">{ann.title}</h3>
                      <Badge variant={ann.active ? 'default' : 'secondary'} className="text-[10px]">
                        {ann.active ? 'Active' : 'Inactive'}
                      </Badge>
                      <Badge variant="outline" className="text-[10px]">
                        {AUDIENCE_OPTIONS.find(a => a.value === ann.audience)?.label || ann.audience}
                      </Badge>
                    </div>
                    {/* Phase 27.9 — preserve paragraph breaks / line
                        wrapping so multi-paragraph announcements read
                        the way they were written. */}
                    <p className="text-sm text-slate-700 mb-2 whitespace-pre-wrap leading-relaxed">{ann.message}</p>
                    <div className="flex items-center gap-3 text-xs text-slate-400">
                      <span>Published: {formatDate(ann.publish_date)}</span>
                      {ann.expiration_date && <span>Expires: {formatDate(ann.expiration_date)}</span>}
                      <span>By: {ann.created_by_name}</span>
                    </div>
                  </div>
                  {isKR && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => toggleActive(ann)} data-testid={`toggle-announcement-${ann.announcement_id}`}>
                        {ann.active ? <EyeOff size={14} /> : <Eye size={14} />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(ann)} data-testid={`edit-announcement-${ann.announcement_id}`}>
                        <Edit size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-500" onClick={() => setDeleteTarget(ann)} data-testid={`delete-announcement-${ann.announcement_id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
          </div>
        )}
        {isKR && tab === 'banners' && <DashboardBannerManager />}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? 'Edit' : 'New'} Announcement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Announcement title" data-testid="ann-title-input" />
            </div>

            {/* Phase 27.9 — Cover image picker. Live preview reflects
                either the persisted cover (edit mode) or the pending
                file selected in create mode. */}
            <div className="space-y-2" data-testid="ann-cover-picker">
              <Label>Cover Image (optional · 16:9)</Label>
              {(editing?.cover_image_url || pendingCover) ? (
                <div className="relative rounded-lg border overflow-hidden bg-slate-50 aspect-video">
                  <img
                    src={pendingCover
                      ? URL.createObjectURL(pendingCover)
                      : `${process.env.REACT_APP_BACKEND_URL}${editing.cover_image_url}`}
                    alt="Cover preview"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={handleCoverDelete}
                    className="absolute top-2 right-2 bg-white/90 rounded-full p-1 hover:bg-white shadow"
                    title="Remove cover"
                    data-testid="ann-cover-remove"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <label className="flex items-center gap-2 border border-dashed border-slate-300 rounded-lg p-3 hover:border-slate-400 cursor-pointer transition-colors">
                  <ImageIcon size={18} className="text-slate-400" />
                  <span className="text-xs text-slate-600">
                    {uploadingCover ? 'Uploading…' : 'Click to upload · Recommended 1600×900 (16:9) · PNG · JPEG · WebP · GIF up to 5 MB'}
                  </span>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    className="sr-only"
                    onChange={handleCoverPick}
                    disabled={uploadingCover}
                    data-testid="ann-cover-input"
                  />
                </label>
              )}
            </div>

            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} placeholder="Announcement content — line breaks are preserved" rows={5} data-testid="ann-message-input" />
              <p className="text-[10px] text-slate-400">Line breaks and blank lines between paragraphs are preserved on the announcement card.</p>
            </div>
            <div className="space-y-2">
              <Label>Audience</Label>
              <Select value={form.audience} onValueChange={v => setForm(p => ({ ...p, audience: v }))}>
                <SelectTrigger data-testid="ann-audience-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AUDIENCE_OPTIONS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.audience === 'specific_agency' && (
              <div className="space-y-2">
                <Label>Target Agency</Label>
                <Select value={form.target_agency_id} onValueChange={v => setForm(p => ({ ...p, target_agency_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select agency" /></SelectTrigger>
                  <SelectContent>
                    {agencies.map(a => <SelectItem key={a.agency_id} value={a.agency_id}>{a.agency_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.audience === 'specific_roles' && (
              <div className="space-y-2">
                <Label>Target Roles</Label>
                <div className="flex flex-wrap gap-2">
                  {ALL_ROLES.map(role => (
                    <Badge key={role} variant={form.target_roles.includes(role) ? 'default' : 'outline'}
                      className="cursor-pointer" onClick={() => toggleRole(role)}>
                      {role.replace(/_/g, ' ')}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Expiration Date (optional)</Label>
                <Input type="date" value={form.expiration_date} onChange={e => setForm(p => ({ ...p, expiration_date: e.target.value }))} data-testid="ann-expiry-input" />
              </div>
              <div className="space-y-2">
                <Label>Active</Label>
                <div className="pt-1">
                  <Switch checked={form.active} onCheckedChange={v => setForm(p => ({ ...p, active: v }))} data-testid="ann-active-switch" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} data-testid="ann-save-btn">{editing ? 'Update' : 'Publish'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Announcement</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete &ldquo;{deleteTarget?.title}&rdquo;. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Phase 27.53 — 16:9 cover cropper */}
      {cropTarget && (
        <ImageCropper16x9
          file={cropTarget}
          onCancel={() => setCropTarget(null)}
          onCropped={handleCropped}
        />
      )}
    </div>
  );
}
